import React from 'react';
import { motion } from 'framer-motion';
import { Sparkles, ArrowRight, Star } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import '../styles/design-system.css';
import heroPhoto from '../assets/hero.png';


const HeroSection = () => {
  const { heroData, contactData, language, theme } = useLanguage();
  const handleNavClick = (href) => {
    const element = document.querySelector(href);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section id="hero" className="hero-section">
      {/* Background Photo - Left Side */}
      <div className="absolute left-0 top-0 bottom-0 w-1/2 hidden lg:block">
        <div className={`absolute inset-0 bg-gradient-to-r from-transparent via-transparent ${theme === 'dark' ? 'to-[#121212]' : 'to-[#FAF7F2]'} z-10`} />
        <motion.img
          initial={{ opacity: 0, scale: 1.1 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 1.2 }}
          src={heroPhoto}
          alt="Lucimar Bizoni"
          className="w-full h-full object-cover object-top"
          style={{ objectPosition: 'center top' }}
        />
      </div>

      {/* Content - Right Side */}
      <div className="container relative z-20">
        <div className="grid lg:grid-cols-2 gap-8 items-center min-h-screen py-24 lg:pt-32 lg:pb-24">
          {/* Empty space for photo on desktop */}
          <div className="hidden lg:block" />

          {/* Content */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.3 }}
            className="text-left lg:pl-8"
          >
            {/* Announcement Badge */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.5 }}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full mb-6"
              style={{ background: 'var(--accent-gold-200)', border: '1px solid var(--accent-gold-400)' }}
            >
              <Sparkles size={14} style={{ color: 'var(--accent-gold-400)' }} />
              <span className="mono-text" style={{ color: 'var(--text-secondary)' }}>
                {heroData.announcement}
              </span>
            </motion.div>

            {/* Hero Title */}
            <motion.h1
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.6 }}
              className="heading-hero mb-4"
            >
              {heroData.title}
            </motion.h1>

            {/* Subtitle */}
            <motion.p
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.7 }}
              className="body-large mb-4"
              style={{ color: 'var(--text-secondary)' }}
            >
              {heroData.subtitle}
            </motion.p>

            {/* Description */}
            <motion.p
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.8 }}
              className="body-medium mb-8"
              style={{ color: 'var(--text-muted)', maxWidth: '480px' }}
            >
              {heroData.description}
            </motion.p>

            {/* CTA Buttons */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.9 }}
              className="flex flex-col sm:flex-row gap-4 mb-10"
            >
              <a
                href={`https://wa.me/${contactData.whatsapp.replace(/\D/g, '')}?text=Olá! Gostaria de agendar uma consultoria.`}
                target="_blank"
                rel="noopener noreferrer"
                className="btn-primary flex items-center justify-center gap-2 text-decoration-none"
              >
                {heroData.ctaPrimary}
                <ArrowRight size={16} />
              </a>
              <button
                onClick={() => handleNavClick('#tools')}
                className="btn-secondary flex items-center justify-center gap-2"
              >
                {heroData.ctaSecondary}
              </button>
            </motion.div>

            {/* Social Proof */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 1 }}
              className="flex items-center gap-6 flex-wrap"
            >
              <div className="flex items-center gap-3">
                <div className="flex -space-x-2">
                  {[1, 2, 3, 4].map((i) => (
                    <div
                      key={i}
                      className="w-9 h-9 rounded-full border-2 overflow-hidden"
                      style={{ borderColor: 'var(--bg-page)', background: 'var(--accent-bronze-200)' }}
                    >
                      <img
                        src={`https://i.pravatar.cc/36?img=${i + 20}`}
                        alt="Avatar"
                        className="w-full h-full object-cover"
                      />
                    </div>
                  ))}
                </div>
                <div>
                  <div className="flex items-center gap-0.5 mb-0.5">
                    {[1, 2, 3, 4, 5].map((i) => (
                      <Star key={i} size={12} fill="#B8860B" color="#B8860B" />
                    ))}
                  </div>
                  <p className="caption">+1000 {language === 'en' ? 'lives transformed' : 'vidas transformadas'}</p>
                </div>
              </div>

              {/* Divider */}
              <div className="hidden sm:block w-px h-10" style={{ background: 'var(--border-light)' }} />

              {/* Stats */}
              <div className="flex gap-6">
                <div>
                  <p className="heading-2" style={{ color: 'var(--accent-gold-400)' }}>21+</p>
                  <p className="caption">{language === 'en' ? 'Years of experience' : 'Anos de experiência'}</p>
                </div>
                <div>
                  <p className="heading-2" style={{ color: 'var(--accent-gold-400)' }}>15+</p>
                  <p className="caption">{language === 'en' ? 'Years of Divas Project' : 'Anos Projeto Divas'}</p>
                </div>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </div>

      {/* Mobile Photo */}
      <div className="lg:hidden absolute inset-0 z-0">
        <div className={`absolute inset-0 bg-gradient-to-b ${theme === 'dark' ? 'from-[#121212]/80 via-[#121212]/60 to-[#121212]' : 'from-[#FAF7F2]/80 via-[#FAF7F2]/60 to-[#FAF7F2]'} z-10`} />
        <img
          src={heroPhoto}
          alt="Lucimar Bizoni"
          className="w-full h-full object-cover opacity-60"
          style={{ objectPosition: 'center top' }}
        />
      </div>

      {/* Decorative Elements */}
      <div className={`absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t ${theme === 'dark' ? 'from-[#121212]' : 'from-[#FAF7F2]'} to-transparent z-10`} />
    </section>
  );
};

export default HeroSection;
